import { PureComponent, ReactNode } from 'react';
import { connect } from 'react-redux';

import { ThunkDispatch } from 'redux-thunk';
import { GlobalState } from '../store/reducers';
import { Actions } from '../store/actions';
import fetchPastVitalsByPtOid, {
    DataColumn,
    Vital,
    VitalHistory,
} from '../api/fetchPastVitalsByPtOid';
import Fuse from 'fuse.js';
import {
    MISC_KEYS,
    getDoc,
    getDoctorSelectorList,
    getItemFromMisc,
    getPatientByLocalId,
} from '../store/idbHandler';
import { track } from '../tracking';
import { starKeyVital } from '../store/actions/session';
import { AnimatePresence } from 'framer-motion';
import {
    HistoricalLabVitalsContent,
    sortKeyvitalsPastVisits,
} from '../components/prescriptionPad/HistoricalLabVitalsContent';
import { requestMedicalRecords } from '../api/requestMedicalRecords';
import { notification } from 'antd';
import { getGenieSetting } from '../store/selectors/session';
import RequestModalRequests from './RequestMedicalRecords';
import { Patient } from '../store/types/patient';
import { getMyDbId } from '../api/mactiveid';

interface Props {
    dispatch: ThunkDispatch<GlobalState, null, Actions>;
    plId: string;
    isABDM?: boolean;
}

interface IPastVitals {
    name: string;
    column: DataColumn[];
    values: Vital[];
    id: string;
}

interface IPastVitals {
    name: string;
    column: DataColumn[];
    values: Vital[];
    id: string;
}

type State = {
    pastVitals?: { [k: string]: VitalHistory };
    fetchStatus?: 'loading' | 'success' | 'error';
    lastUpdated?: string;
    vitalSearch: string;
    pastVitalsFormatted?: IPastVitals[];
    keyVitals?: string[];
    requestMRModalOpen: boolean;
    patient?: Patient;
};

const fuseConfig = {
    keys: ['name'],
    threshold: 0.2,
};

class PastVitalsRightPanel extends PureComponent<Props, State> {
    state: State = {
        vitalSearch: '',
        keyVitals: undefined,
        requestMRModalOpen: false,
        patient: undefined,
    };

    fuse = new Fuse([] as IPastVitals[], fuseConfig);

    componentDidMount = async (): Promise<void> => {
        getItemFromMisc<string[]>(getMyDbId() || '', MISC_KEYS.keyVitals)
            .then((keyVitals) => {
                this.setState({ keyVitals });
            })
            .catch((e) => console.error(e));
        this.setUp();
    };

    setUp = async () => {
        this.setState({
            fetchStatus: 'loading',
        });
        try {
            const pastVitals = await fetchPastVitalsByPtOid(this.props.plId);

            if (pastVitals.status !== 'success' || !pastVitals.data.vitalData) {
                this.setState({
                    // fetchStatus: 'error',
                });
                alert('Failed to fetch past vital data');
                return;
            }

            const pastVitalsFormatted: IPastVitals[] = Object.keys(pastVitals.data.vitalData).map(
                (id) => ({
                    id,
                    ...pastVitals.data.vitalData[id],
                }),
            );

            this.fuse = new Fuse(pastVitalsFormatted || [], fuseConfig);

            const keys = Object.keys(pastVitals.data.vitalData);
            this.setState({
                pastVitals: pastVitals.data.vitalData,
                fetchStatus: 'success',
                lastUpdated: new Date().toISOString(),
                pastVitalsFormatted,
            });
        } catch (e) {
            console.error(e);
            alert('Failed to fetch past vital data');
        }
    };

    componentDidUpdate = (prevProps: Props): void => {
        if (prevProps.plId !== this.props.plId) {
            this.setUp();
        }
    };

    toggleKeyVital = async (vitalId: string): Promise<void> => {
        const currKeyVitals = this.state.keyVitals ?? [];

        let newKeyVitals = [];

        const doesExist = currKeyVitals.includes(vitalId);

        if (doesExist) {
            newKeyVitals = currKeyVitals.filter((v) => v !== vitalId);
        } else {
            newKeyVitals = [...currKeyVitals, vitalId];
        }

        this.setState({
            keyVitals: newKeyVitals,
            // sortKeyVitals: false,
        });

        track('UPDATE_STAR_VITALS', { from: 'vital-history-page-patient-details' });

        this.props.dispatch(starKeyVital(newKeyVitals));
    };

    setVitalSearch = (vital: string): void => {
        this.setState({ vitalSearch: vital });
    };

    onRequestMR = async () => {
        const { plId, isABDM } = this.props;
        const patient = await getPatientByLocalId(plId);
        this.setState({ patient });

        track('REQUEST_MEDICAL_RECORDS', {
            isABDM: isABDM?.toString(),
            abhaId: patient?.abha?.id,
            consentRequest: (patient?.abha && patient?.abha.id)?.toString(),
            on: 'pt-vitals-rp',
        });

        if (isABDM === false) {
            requestMedicalRecords(getDoctorSelectorList()?.[0].id, plId);
            return;
        }

        if (patient?.abha?.id) {
            this.openRequestMedicalRecords();
            return;
        }

        notification.error({
            message: 'Patient Health Id not found. Please try again or refresh!',
            duration: 5,
            placement: 'topRight',
        });
    };

    openRequestMedicalRecords = () => {
        this.setState({ requestMRModalOpen: true });
    };

    closeRequestMedicalRecords = () => {
        this.setState({ requestMRModalOpen: false });
    };

    render(): ReactNode {
        const { pastVitals, fetchStatus, requestMRModalOpen, patient } = this.state;

        const { plId } = this.props;

        if (fetchStatus === 'loading') {
            return (
                <div className="flex flex-col col-span-10 items-center justify-center w-full h-full space-y-64">
                    <div className="pulse">
                        <svg width="16px" height="12px">
                            <polyline id="back" points="1 6 4 6 6 11 10 1 12 6 15 6"></polyline>
                            <polyline id="front" points="1 6 4 6 6 11 10 1 12 6 15 6"></polyline>
                        </svg>
                    </div>
                    <p className="text-24 text-neutral-black">Fetching past vitals...</p>
                </div>
            );
        }

        if (!pastVitals) {
            return null;
        }

        const fData = this.state.vitalSearch
            ? this.fuse.search(this.state.vitalSearch).map((t) => t.item)
            : this.state.pastVitalsFormatted || [];

        const sortedFData =
            this.state.keyVitals && this.state.keyVitals.length !== 0
                ? sortKeyvitalsPastVisits(fData, this.state.keyVitals)
                : fData;

        const columnCount = (fData || []).reduce(
            (max, item) => Math.max(max, item.values.length),
            0,
        );

        const doc = getDoc();

        return (
            <div className="flex col-span-10 overflow-hidden bg-white w-full">
                <HistoricalLabVitalsContent
                    fData={sortedFData}
                    maxCount={columnCount}
                    vitalSearch={this.state.vitalSearch}
                    setSearchInput={this.setVitalSearch}
                    headingText="Vitals & lab results"
                    toggleKeyVital={this.toggleKeyVital}
                    keyVitals={this.state.keyVitals}
                    onRequestMR={this.onRequestMR}
                    patientId={plId}
                />
                <AnimatePresence>
                    {requestMRModalOpen && patient && (
                        <RequestModalRequests
                            closeMe={this.closeRequestMedicalRecords}
                            doctorprofile={doc}
                            patientDirectory={patient}
                            isModalOpen={requestMRModalOpen}
                            plId={plId}
                        />
                    )}
                </AnimatePresence>
            </div>
        );
    }
}

export default connect((state: GlobalState) => ({
    isABDM: getGenieSetting(state, 'abdm') as boolean,
}))(PastVitalsRightPanel);

const TEMPERATURE_ICON = (
    <svg
        xmlns="http://www.w3.org/2000/svg"
        viewBox="0 0 512 512"
        width="16"
        height="16"
        className="fill-actionPrimary"
    >
        <path d="M476.8 20.4c-37.5-30.7-95.5-26.3-131.9 10.2l-45.7 46 50.5 50.5c3.1 3.1 3.1 8.2 0 11.3l-11.3 11.3c-3.1 3.1-8.2 3.1-11.3 0l-50.4-50.5-45.1 45.4 50.3 50.4c3.1 3.1 3.1 8.2 0 11.3l-11.3 11.3c-3.1 3.1-8.2 3.1-11.3 0L209 167.4l-45.1 45.4L214 263c3.1 3.1 3.1 8.2 0 11.3l-11.3 11.3c-3.1 3.1-8.2 3.1-11.3 0l-50.1-50.2L96 281.1V382L7 471c-9.4 9.4-9.4 24.6 0 33.9 9.4 9.4 24.6 9.4 33.9 0l89-89h99.9L484 162.6c34.9-34.9 42.2-101.5-7.2-142.2z"></path>
    </svg>
);

const SPO2_ICON = (
    <svg
        xmlns="http://www.w3.org/2000/svg"
        viewBox="0 0 640 512"
        width="16"
        height="16"
        className="fill-actionPrimary"
    >
        <path d="M636.11 390.15C614.44 308.85 580.07 231 534.1 159.13 511.98 124.56 498.03 96 454.05 96 415.36 96 384 125.42 384 161.71v60.11l-32.88-21.92a15.996 15.996 0 0 1-7.12-13.31V16c0-8.84-7.16-16-16-16h-16c-8.84 0-16 7.16-16 16v170.59c0 5.35-2.67 10.34-7.12 13.31L256 221.82v-60.11C256 125.42 224.64 96 185.95 96c-43.98 0-57.93 28.56-80.05 63.13C59.93 231 25.56 308.85 3.89 390.15 1.3 399.84 0 409.79 0 419.78c0 61.23 62.48 105.44 125.24 88.62l59.5-15.95c42.18-11.3 71.26-47.47 71.26-88.62v-87.49l-85.84 57.23a7.992 7.992 0 0 1-11.09-2.22l-8.88-13.31a7.992 7.992 0 0 1 2.22-11.09L320 235.23l167.59 111.72a7.994 7.994 0 0 1 2.22 11.09l-8.88 13.31a7.994 7.994 0 0 1-11.09 2.22L384 316.34v87.49c0 41.15 29.08 77.31 71.26 88.62l59.5 15.95C577.52 525.22 640 481.01 640 419.78c0-9.99-1.3-19.94-3.89-29.63z" />
    </svg>
);

const BP_ICON = (
    <svg
        xmlns="http://www.w3.org/2000/svg"
        viewBox="0 0 352 512"
        width="16"
        height="16"
        className="fill-actionPrimary"
    >
        <path d="M205.22 22.09c-7.94-28.78-49.44-30.12-58.44 0C100.01 179.85 0 222.72 0 333.91 0 432.35 78.72 512 176 512s176-79.65 176-178.09c0-111.75-99.79-153.34-146.78-311.82zM176 448c-61.75 0-112-50.25-112-112 0-8.84 7.16-16 16-16s16 7.16 16 16c0 44.11 35.89 80 80 80 8.84 0 16 7.16 16 16s-7.16 16-16 16z"></path>
    </svg>
);

const SUGAR_ICON = (
    <svg
        xmlns="http://www.w3.org/2000/svg"
        viewBox="0 0 512 512"
        width="16"
        height="16"
        className="fill-actionPrimary"
    >
        <path d="M239.1 7.5l-208 78c-18.7 7-31.1 25-31.1 45v225.1c0 18.2 10.3 34.8 26.5 42.9l208 104c13.5 6.8 29.4 6.8 42.9 0l208-104c16.3-8.1 26.5-24.8 26.5-42.9V130.5c0-20-12.4-37.9-31.1-44.9l-208-78C262 3.4 250 3.4 239.1 7.5zm16.9 45l208 78v.3l-208 84.5-208-84.5v-.3l208-78zM48 182.6l184 74.8v190.2l-184-92v-173zm232 264.9V257.4l184-74.8v172.9l-184 92z"></path>
    </svg>
);

export const ICON_MAP: { [k: string]: ReactNode } = {
    'lb-199711567': TEMPERATURE_ICON,
    'lb-1771358750': SPO2_ICON,
    'lb-8296133834': BP_ICON,
    'lb-9242267093': BP_ICON,
};

type PP = {
    vitalHistory: VitalHistory;
};

export const PastVitalsTable: React.FC<PP> = ({ vitalHistory }) => {
    const order = vitalHistory.column.map((c) => c.key);
    return (
        <div className="flex flex-col bg-white">
            <div className="flex flex-col flex-auto p-16 space-y-16 overflow-hidden">
                <div>
                    <p className="leading-6 text-18 text-text-primary font-700">
                        {vitalHistory.name}
                    </p>
                </div>

                <div className="flex-auto overflow-auto">
                    <table className="w-full">
                        <tbody>
                            <tr>
                                {vitalHistory.column.map((c) => (
                                    <td
                                        key={c.key}
                                        className="sticky top-0 px-16 py-6 uppercase text-11 font-700 text-textColor-secondary bg-background-secondary"
                                    >
                                        <p>{c.val}</p>
                                    </td>
                                ))}
                            </tr>
                            {vitalHistory.values.map((row, i) => (
                                <tr key={`${vitalHistory.name}-${i}`}>
                                    {order.map((o, ii) => (
                                        <td
                                            key={`${o}-${ii}`}
                                            className={`px-16 py-6 text-14 text-textColor-secondary ${o}`}
                                        >
                                            <p>{row[o]}</p>
                                        </td>
                                    ))}
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            </div>
        </div>
    );
};
